import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Terminal, MessageSquare, Info } from 'lucide-react';
import { tools } from '../../tools';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const location = useLocation();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-64 z-50
          bg-[var(--bg-secondary)] border-r border-[var(--border-color)]
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0 lg:sticky lg:h-screen lg:z-auto
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-[var(--border-color)]">
            <Link to="/" className="flex items-center gap-2" onClick={onToggle}>
              <Terminal className="w-6 h-6 text-[var(--accent)]" />
              <span className="font-bold text-[var(--text-primary)]">
                secopstools.org
              </span>
            </Link>
            <button
              className="lg:hidden p-1 rounded hover:bg-[var(--bg-tertiary)]"
              onClick={onToggle}
              aria-label="Close sidebar"
            >
              <X className="w-5 h-5 text-[var(--text-secondary)]" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-3">
              Tools
            </div>
            <ul className="space-y-1">
              {tools.map((tool) => {
                const isActive = location.pathname === tool.path;
                const Icon = tool.icon;
                
                return (
                  <li key={tool.id}>
                    <Link
                      to={tool.path}
                      className={`sidebar-link ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        if (window.innerWidth < 1024) {
                          onToggle();
                        }
                      }}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tool.name}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Contact & About */}
          <div className="p-4 border-t border-[var(--border-color)]">
            <Link
              to="/about"
              className={`sidebar-link ${location.pathname === '/about' ? 'active' : ''}`}
              onClick={() => {
                if (window.innerWidth < 1024) {
                  onToggle();
                }
              }}
            >
              <Info className="w-4 h-4" />
              <span>About</span>
            </Link>
            <Link
              to="/contact"
              className={`sidebar-link ${location.pathname === '/contact' ? 'active' : ''}`}
              onClick={() => {
                if (window.innerWidth < 1024) {
                  onToggle();
                }
              }}
            >
              <MessageSquare className="w-4 h-4" />
              <span>Contact</span>
            </Link>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-[var(--border-color)]">
            <div className="text-xs text-[var(--text-muted)]">
              <span className="text-[var(--accent)]">$</span> secopstools.org
              <span className="cursor-blink"></span>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile menu button */}
      <button
        className={`
          fixed top-4 left-3 z-50 p-2 rounded-lg
          bg-[var(--bg-secondary)] border border-[var(--border-color)]
          shadow-lg lg:hidden
          ${isOpen ? 'hidden' : ''}
        `}
        onClick={onToggle}
        aria-label="Open sidebar"
      >
        <Menu className="w-5 h-5 text-[var(--text-secondary)]" />
      </button>
    </>
  );
}
